import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { DateTime } from 'luxon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchProgramsForParams } from '../../api/programs';
import { getReturningKidsTrend } from '../../api/admin';
import { SEASONS } from '../../constants/constants';
import Loader from '../common/Loader';
import './Metrics.css';

const currentYear = DateTime.now().year;
const yearOptions = [
  { value: 'all', label: 'All Years' },
  ...Array.from({ length: currentYear - 2024 + 2 }, (_, i) => ({
    value: 2024 + i,
    label: String(2024 + i)
  }))
];

const seasonOptions = SEASONS.map(s => ({ value: s.toLowerCase(), label: s }));

const SEASON_ORDER = ['winter', 'spring', 'summer', 'fall'];

const KPI_INFO = {
  // Programs Section
  activePrograms: {
    title: 'Active Programs',
    description: 'Programs that are currently running or confirmed to run.',
    calculation: 'Count of programs with status: confirmed or active',
    format: 'number',
    color: '#28a745'
  },
  totalProposed: {
    title: 'Total Proposed',
    description: 'All programs proposed for this period, excluding test programs.',
    calculation: 'Count of all programs except those with status: test',
    format: 'number',
    color: '#007bff'
  },
  completedPrograms: {
    title: 'Completed Programs',
    description: 'Programs that have finished and been processed.',
    calculation: 'Count of programs with status: complete, invoiced, or paid',
    format: 'number',
    color: '#17a2b8'
  },
  notCanceledPercent: {
    title: '% Not Canceled',
    description: 'Percentage of proposed programs that were not canceled.',
    calculation: '(Programs not canceled ÷ Total proposed) × 100',
    format: 'percent',
    color: '#6f42c1'
  },
  // Registration Section
  totalRegistrations: {
    title: 'Total Registrations',
    description: 'Total number of kids registered across all programs.',
    calculation: 'Sum of registrationCount for all programs',
    format: 'number',
    color: '#28a745'
  },
  nonCanceledRegistrations: {
    title: 'Non-Canceled Registrations',
    description: 'Registrations in programs that were not canceled.',
    calculation: 'Sum of registrationCount for non-canceled programs',
    format: 'number',
    color: '#007bff'
  },
  registrationRetentionPercent: {
    title: '% Retained',
    description: 'Percentage of registrations in non-canceled programs.',
    calculation: '(Non-canceled registrations ÷ Total registrations) × 100',
    format: 'percent',
    color: '#6f42c1'
  },
  avgRegistration: {
    title: 'Avg Registration',
    description: 'Average number of registrations per active or completed program.',
    calculation: 'Total registrations in active/completed programs ÷ Number of active/completed programs',
    format: 'decimal',
    color: '#fd7e14'
  },
  // Revenue Section
  totalRevenue: {
    title: 'Total Revenue',
    description: 'Estimated revenue from completed programs.',
    calculation: 'Sum of (price × registrationCount) for programs with status: invoiced, paid, or complete',
    format: 'currency',
    color: '#28a745'
  },
  // Retention Section
  returningKids: {
    title: 'Returning Kids',
    description: 'Kids enrolled in non-canceled programs who were also in a non-canceled program in a prior season.',
    calculation: 'Count of unique kids in non-canceled programs this period who appeared in any non-canceled program before this period',
    format: 'number',
    color: '#20c997'
  },
  returningKidsPercent: {
    title: '% Returning',
    description: 'Percentage of enrolled kids this period who previously participated.',
    calculation: '(Returning kids ÷ Total unique kids in non-canceled programs) × 100',
    format: 'percent',
    color: '#fd7e14'
  }
};

// Calculate metrics for a given set of programs
function calculateMetrics(programs) {
  const activePrograms = programs.filter(p =>
    p.status === 'confirmed' || p.status === 'active'
  ).length;

  const proposedPrograms = programs.filter(p => p.status !== 'test');
  const totalProposed = proposedPrograms.length;

  const completedPrograms = programs.filter(p =>
    p.status === 'complete' || p.status === 'invoiced' || p.status === 'paid'
  ).length;

  const nonCanceledPrograms = proposedPrograms.filter(p => p.status !== 'canceled');
  const notCanceledPercent = totalProposed > 0
    ? (nonCanceledPrograms.length / totalProposed) * 100
    : 0;

  const totalRegistrations = programs.reduce((sum, p) => sum + (p.registrationCount || 0), 0);

  const nonCanceledRegistrations = programs
    .filter(p => p.status !== 'canceled')
    .reduce((sum, p) => sum + (p.registrationCount || 0), 0);

  const registrationRetentionPercent = totalRegistrations > 0
    ? (nonCanceledRegistrations / totalRegistrations) * 100
    : 0;

  const activeOrCompletedPrograms = programs.filter(p =>
    p.status === 'confirmed' || p.status === 'active' ||
    p.status === 'complete' || p.status === 'invoiced' || p.status === 'paid'
  );
  const activeOrCompletedRegistrations = activeOrCompletedPrograms.reduce(
    (sum, p) => sum + (p.registrationCount || 0), 0
  );
  const avgRegistration = activeOrCompletedPrograms.length > 0
    ? activeOrCompletedRegistrations / activeOrCompletedPrograms.length
    : 0;

  const totalRevenue = programs
    .filter(p => p.status === 'invoiced' || p.status === 'paid' || p.status === 'complete')
    .reduce((sum, p) => sum + ((p.price || 0) * (p.registrationCount || 0)), 0);

  return {
    activePrograms,
    totalProposed,
    completedPrograms,
    notCanceledPercent,
    totalRegistrations,
    nonCanceledRegistrations,
    registrationRetentionPercent,
    avgRegistration,
    totalRevenue
  };
}

function KpiCard({ type, value, isSelected, onClick }) {
  const info = KPI_INFO[type];
  if (!info) return null;

  return (
    <div
      className={`kpi-card ${type} ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="kpi-header">
        <h3>{info.title}</h3>
        <div className="kpi-tooltip-wrapper" onClick={(e) => e.stopPropagation()}>
          <span className="kpi-info-icon">i</span>
          <div className="kpi-tooltip">
            <p className="kpi-tooltip-description">{info.description}</p>
            <p className="kpi-tooltip-calculation">
              <strong>Calculation:</strong> {info.calculation}
            </p>
          </div>
        </div>
      </div>
      <p className="kpi-value">{value}</p>
      {isSelected && <div className="selected-indicator">Showing trend below</div>}
    </div>
  );
}

function Metrics() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('totalRegistrations');
  const [returningKidsTrend, setReturningKidsTrend] = useState([]);

  // Filters
  const [yearFilter, setYearFilter] = useState({ value: currentYear, label: String(currentYear) });
  const [seasonFilter, setSeasonFilter] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const params = {};
        if (yearFilter && yearFilter.value !== 'all') {
          params.year = yearFilter.value;
        }
        if (seasonFilter?.length) {
          params.season = seasonFilter.map(s => s.value);
        }

        const response = await fetchProgramsForParams(params);

        if (response?.data) {
          setPrograms(response.data);
        }
      } catch (error) {
        console.error('Error fetching programs:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [yearFilter, seasonFilter]);

  useEffect(() => {
    getReturningKidsTrend().then(res => {
      if (res?.data) setReturningKidsTrend(res.data);
    });
  }, []);

  // Calculate current metrics
  const metrics = useMemo(() => calculateMetrics(programs), [programs]);

  // Derive returning kids KPI values for current filter
  const returningMetrics = useMemo(() => {
    const filtered = returningKidsTrend.filter(d => {
      const yearMatch = !yearFilter || yearFilter.value === 'all' || d.year === yearFilter.value;
      const seasonMatch = !seasonFilter?.length || seasonFilter.some(s => s.value === d.season);
      return yearMatch && seasonMatch;
    });
    const total = filtered.reduce((s, d) => s + d.total, 0);
    const returning = filtered.reduce((s, d) => s + d.returning, 0);
    return {
      returningKids: returning,
      returningKidsPercent: total > 0 ? (returning / total) * 100 : 0
    };
  }, [returningKidsTrend, yearFilter, seasonFilter]);

  // Calculate trend data grouped by year and season
  const trendData = useMemo(() => {
    // Build lookup from returningKidsTrend by year-season
    const returningByPeriod = Object.fromEntries(
      returningKidsTrend.map(d => [`${d.year}-${d.season}`, d])
    );

    // Group programs by year and season
    const grouped = {};
    programs.forEach(p => {
      const key = `${p.year}-${p.season}`;
      if (!grouped[key]) {
        grouped[key] = { year: p.year, season: p.season, programs: [] };
      }
      grouped[key].programs.push(p);
    });

    // Calculate metrics for each group and sort
    const data = Object.values(grouped)
      .map(group => {
        const returningData = returningByPeriod[`${group.year}-${group.season}`] || {};
        return {
          label: `${group.season.charAt(0).toUpperCase() + group.season.slice(1)} ${group.year}`,
          year: group.year,
          season: group.season,
          sortKey: group.year * 10 + SEASON_ORDER.indexOf(group.season),
          ...calculateMetrics(group.programs),
          returningKids: returningData.returning ?? 0,
          returningKidsPercent: returningData.returningPercent ?? 0
        };
      })
      .sort((a, b) => a.sortKey - b.sortKey);

    return data;
  }, [programs, returningKidsTrend]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value / 100);
  };

  const formatNumber = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const formatValue = (type, value) => {
    const info = KPI_INFO[type];
    if (!info) return value;

    switch (info.format) {
      case 'currency':
        return formatCurrency(value);
      case 'percent':
        return formatPercent(value);
      case 'decimal':
        return formatNumber(value, 1);
      default:
        return formatNumber(value);
    }
  };

  const formatTooltipValue = (value) => {
    const info = KPI_INFO[selectedMetric];
    if (!info) return value;

    switch (info.format) {
      case 'currency':
        return formatCurrency(value);
      case 'percent':
        return `${value.toFixed(1)}%`;
      case 'decimal':
        return value.toFixed(1);
      default:
        return Math.round(value).toLocaleString();
    }
  };

  if (loading) {
    return <Loader size={100} />;
  }

  const selectedInfo = KPI_INFO[selectedMetric];

  return (
    <div className="metrics-page">
      <h1>Metrics</h1>

      <div className="metrics-filters">
        <div className="metrics-filter-group">
          <label>Year</label>
          <Select
            value={yearFilter}
            onChange={setYearFilter}
            options={yearOptions}
            className="metrics-filter-select"
            classNamePrefix="filter"
          />
        </div>
        <div className="metrics-filter-group">
          <label>Season</label>
          <Select
            value={seasonFilter}
            onChange={setSeasonFilter}
            options={seasonOptions}
            isMulti
            placeholder="All"
            className="metrics-filter-select"
            classNamePrefix="filter"
          />
        </div>
      </div>

      <section className="metrics-section">
        <h2>Programs</h2>
        <div className="kpi-grid">
          <KpiCard
            type="activePrograms"
            value={metrics.activePrograms}
            isSelected={selectedMetric === 'activePrograms'}
            onClick={() => setSelectedMetric('activePrograms')}
          />
          <KpiCard
            type="totalProposed"
            value={metrics.totalProposed}
            isSelected={selectedMetric === 'totalProposed'}
            onClick={() => setSelectedMetric('totalProposed')}
          />
          <KpiCard
            type="completedPrograms"
            value={metrics.completedPrograms}
            isSelected={selectedMetric === 'completedPrograms'}
            onClick={() => setSelectedMetric('completedPrograms')}
          />
          <KpiCard
            type="notCanceledPercent"
            value={formatValue('notCanceledPercent', metrics.notCanceledPercent)}
            isSelected={selectedMetric === 'notCanceledPercent'}
            onClick={() => setSelectedMetric('notCanceledPercent')}
          />
        </div>
      </section>

      <section className="metrics-section">
        <h2>Registrations</h2>
        <div className="kpi-grid">
          <KpiCard
            type="totalRegistrations"
            value={formatNumber(metrics.totalRegistrations)}
            isSelected={selectedMetric === 'totalRegistrations'}
            onClick={() => setSelectedMetric('totalRegistrations')}
          />
          <KpiCard
            type="nonCanceledRegistrations"
            value={formatNumber(metrics.nonCanceledRegistrations)}
            isSelected={selectedMetric === 'nonCanceledRegistrations'}
            onClick={() => setSelectedMetric('nonCanceledRegistrations')}
          />
          <KpiCard
            type="registrationRetentionPercent"
            value={formatValue('registrationRetentionPercent', metrics.registrationRetentionPercent)}
            isSelected={selectedMetric === 'registrationRetentionPercent'}
            onClick={() => setSelectedMetric('registrationRetentionPercent')}
          />
          <KpiCard
            type="avgRegistration"
            value={formatNumber(metrics.avgRegistration, 1)}
            isSelected={selectedMetric === 'avgRegistration'}
            onClick={() => setSelectedMetric('avgRegistration')}
          />
        </div>
      </section>

      <section className="metrics-section">
        <h2>Revenue</h2>
        <div className="kpi-grid single">
          <KpiCard
            type="totalRevenue"
            value={formatCurrency(metrics.totalRevenue)}
            isSelected={selectedMetric === 'totalRevenue'}
            onClick={() => setSelectedMetric('totalRevenue')}
          />
        </div>
      </section>

      <section className="metrics-section">
        <h2>Retention</h2>
        <div className="kpi-grid">
          <KpiCard
            type="returningKids"
            value={formatNumber(returningMetrics.returningKids)}
            isSelected={selectedMetric === 'returningKids'}
            onClick={() => setSelectedMetric('returningKids')}
          />
          <KpiCard
            type="returningKidsPercent"
            value={formatValue('returningKidsPercent', returningMetrics.returningKidsPercent)}
            isSelected={selectedMetric === 'returningKidsPercent'}
            onClick={() => setSelectedMetric('returningKidsPercent')}
          />
        </div>
      </section>

      {trendData.length > 1 && (
        <section className="metrics-section trend-section">
          <h2>{selectedInfo?.title} Trend</h2>
          <div className="trend-chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    if (selectedInfo?.format === 'currency') {
                      return `$${(value / 1000).toFixed(0)}k`;
                    }
                    if (selectedInfo?.format === 'percent') {
                      return `${value.toFixed(0)}%`;
                    }
                    return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value;
                  }}
                />
                <Tooltip
                  formatter={(value) => [formatTooltipValue(value), selectedInfo?.title]}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey={selectedMetric}
                  stroke={selectedInfo?.color || '#007bff'}
                  strokeWidth={2}
                  dot={{ fill: selectedInfo?.color || '#007bff', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
}

export default Metrics;
